import { createFileRoute } from '@tanstack/react-router';
import {
  Breadcrumbs,
  Box,
  Button,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/joy';
import { AxiosError } from 'axios';
import { BuyEditProvider, useBuyEdit } from '@/contexts/BuyEditContext';
import { CreateEditBuyForm } from '@/components/Pages/Buys/Create/CreateEditBuyForm';
import { Link } from '@tanstack/react-router';
import { QueryResponse, OrderBuyResponseDTO } from '@/types';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/axios';

export const Route = createFileRoute('/_protected/buys/$id')({
  component: BuyDetail,
});

function BuyDetail() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { isSuperAdmin, isAdmin } = useAuth();

  const { data: orderBuy, isLoading } = useQuery<
    QueryResponse<OrderBuyResponseDTO>,
    AxiosError,
    OrderBuyResponseDTO
  >({
    queryKey: ['orderBuys', id],
    queryFn: () => api.get(`/orderBuy/${id}`),
    enabled: !!id,
    select: ({ data }) => data.data,
  });

  const { mutate: confirmOrderBuy, isPending: isConfirmingOrderBuy } =
    useMutation({
      mutationFn: () => api.put(`/orderBuy/${id}/confirm`),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['suppliers', orderBuy!.supplierId],
        });
        queryClient.invalidateQueries({ queryKey: ['orderBuys'] });
        queryClient.invalidateQueries({ queryKey: ['stock'] });
        queryClient.invalidateQueries({ queryKey: ['articles'] });
      },
      onError: () => {
        toast.error('Error al confirmar la compra');
      },
    });

  const { mutate: cancelOrderBuy, isPending: isCancellingOrderBuy } =
    useMutation({
      mutationFn: () => api.put(`/orderBuy/${id}/cancel`),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['suppliers', orderBuy!.supplierId],
        });
        queryClient.invalidateQueries({ queryKey: ['orderBuys'] });
      },
      onError: () => {
        toast.error('Error al cancelar la compra');
      },
    });

  const { mutate: markAsPaid, isPending: isMarkingAsPaid } = useMutation({
    mutationFn: () => api.put(`/orderBuy/${id}/paid`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['suppliers', orderBuy!.supplierId],
      });
      queryClient.invalidateQueries({ queryKey: ['orderBuys'] });

      toast.success('Orden de compra marcada como pagada');
    },
    onError: () => {
      toast.error('Error al marcar la orden como pagada');
    },
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!orderBuy) {
    return <div>Orden de compra no encontrada</div>;
  }

  const BuyDetailWithEditContext = ({
    orderBuy,
  }: {
    orderBuy: OrderBuyResponseDTO;
  }) => {
    const { isEdit, setIsEdit } = useBuyEdit();

    return (
      <>
        <Stack direction='row' justifyContent='space-between' mb={2}>
          <Breadcrumbs size='sm'>
            <Link to='/buys'>Compras</Link>
            <Typography>{id}</Typography>
          </Breadcrumbs>

          {(isSuperAdmin || isAdmin) && (
            <Stack direction='row' gap={2}>
              {isEdit ? (
                <Button
                  variant='outlined'
                  color='neutral'
                  onClick={() => setIsEdit(false)}
                >
                  Cancelar
                </Button>
              ) : (
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={() => setIsEdit(true)}
                  disabled={orderBuy.status !== 'open'}
                >
                  Editar
                </Button>
              )}
              {orderBuy.status === 'open' && (
                <Button
                  variant='outlined'
                  color='warning'
                  onClick={() => confirmOrderBuy()}
                  loading={isConfirmingOrderBuy}
                  disabled={isEdit}
                >
                  Confirmar
                </Button>
              )}
              {orderBuy.status === 'confirm' && (
                <Button
                  variant='outlined'
                  color='success'
                  onClick={() => markAsPaid()}
                  loading={isMarkingAsPaid}
                  disabled={isEdit}
                >
                  Marcar como Pagado
                </Button>
              )}
              {orderBuy.status === 'open' && (
                <Button
                  variant='outlined'
                  color='danger'
                  onClick={() => cancelOrderBuy()}
                  loading={isCancellingOrderBuy}
                  disabled={isEdit}
                >
                  Cancelar
                </Button>
              )}
            </Stack>
          )}
        </Stack>

        <CreateEditBuyForm orderBuy={orderBuy} />
      </>
    );
  };

  return (
    <BuyEditProvider>
      <BuyDetailWithEditContext orderBuy={orderBuy} />
    </BuyEditProvider>
  );
}
